import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { METRIC_CARDS_KEY_PREFIX } from "../shared/constants";
import { MetricCard } from "../shared/MetricCard";

import { HealthSummaryBanner } from "./HealthSummaryBanner";
import { SegmentedScale } from "./SegmentedScale";
import { useHealthDynamicsCard } from "./useHealthDynamicsCard";

const KEY_PREFIX = `${METRIC_CARDS_KEY_PREFIX}.health`;

const SKELETON_ROWS = [0, 1, 2];
const SKELETON_COLUMNS = [0, 1, 2, 3];

const HealthDynamicsSkeleton = () => (
    <Stack
        flex="1"
        gap="20px"
        padding="24px"
    >
        <PixelPlaceholderLoader
            width="100%"
            height={52}
            borderRadius="6px"
        />
        <Stack gap="14px">
            {SKELETON_ROWS.map((row) => (
                <Flex
                    key={row}
                    gap="12px"
                >
                    {SKELETON_COLUMNS.map((column) => (
                        <Box
                            key={column}
                            flex="1"
                        >
                            <PixelPlaceholderLoader
                                width="100%"
                                height={24}
                                borderRadius="4px"
                            />
                        </Box>
                    ))}
                </Flex>
            ))}
        </Stack>
    </Stack>
);

export const HealthDynamicsCard = () => {
    const t = useTranslation();
    const { data, isLoading, isError } = useHealthDynamicsCard();

    return (
        <MetricCard
            title={t(`${KEY_PREFIX}.title`)}
            infoText={t(`${KEY_PREFIX}.tooltip`)}
            dataAutomation="search-trends-metric-card-health"
            isLoading={isLoading}
            isEmpty={isError || !data}
            loadingTemplate={<HealthDynamicsSkeleton />}
        >
            {data ? (
                <Stack
                    flex="1"
                    gap="24px"
                    padding="24px"
                >
                    <HealthSummaryBanner
                        title={t(data.summaryTitleKey)}
                        sentiment={data.sentiment}
                    />
                    <Stack gap="20px">
                        {data.indicators.map((indicator) => (
                            <SegmentedScale
                                key={indicator.id}
                                label={t(indicator.labelKey)}
                                tooltipText={t(indicator.tooltipKey)}
                                leftLabel={t(indicator.leftLabelKey)}
                                rightLabel={t(indicator.rightLabelKey)}
                                segmentLabels={indicator.segmentLabelKeys.map((key) => t(key))}
                                filledIndex={indicator.filledIndex}
                                dataAutomation={`search-trends-health-indicator-${indicator.id}`}
                            />
                        ))}
                    </Stack>
                </Stack>
            ) : null}
        </MetricCard>
    );
};
